import React, { createContext, useContext, useEffect, useState } from 'react';
import pb from '../lib/pocketbase';

/**
 * @typedef {Object} CompanyContextValue
 * @property {string | null} currentCompanyId
 * @property {Object | null} currentCompany
 * @property {Object[]} accessibleCompanies
 * @property {Object[]} otherCompanies
 * @property {(companyId: string) => void} switchCompany
 * @property {boolean} loading
 * @property {string | null} error
 */

const CompanyContext = createContext(
  /** @type {CompanyContextValue} */ ({
    currentCompanyId: null,
    currentCompany: null,
    accessibleCompanies: [],
    otherCompanies: [],
    switchCompany: () => {},
    loading: false,
    error: null,
  })
);

export const CompanyProvider = ({ children }) => {
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [accessibleCompanies, setAccessibleCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load companies for the logged-in user
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        setError(null);

        const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
        const userRecord = pbAuth?.record;

        if (!userRecord) {
          setAccessibleCompanies([]);
          setCurrentCompanyId(null);
          setCurrentCompany(null);
          return;
        }

        const userId = userRecord.id;
        const defaultCompanyId = userRecord.CompanyID || null;

        // 1. Find all Customers where this user is SuperAdmin, Administrator, or ARCChief
        console.log("userId", userId);
        // "SuperAdmin", "Administrator", and "ARCChief" are multiple (array) relation fields
        // So we have to use filter for array fields with ~ (contains) operator
        const customersList = await pb.collection('Customers').getList(1, 50, {
          filter: `SuperAdmin~"${userId}" || Administrator~"${userId}" || ARCChief~"${userId}"`,
        });
        console.log("customersList", customersList);
        const companies = customersList?.items || [];
        setAccessibleCompanies(companies);

        // 2. Determine current company
        let initialCompanyId = defaultCompanyId;

        // If default company is not set or not in accessible list, fall back to the first accessible one
        if (!initialCompanyId || !companies.find((c) => c.id === initialCompanyId)) {
          initialCompanyId = companies[0]?.id || null;
        }

        setCurrentCompanyId(initialCompanyId);

        // Fetch the current company record if we have an ID
        if (initialCompanyId) {
          try {
            const company = await pb.collection('Customers').getOne(initialCompanyId);
            setCurrentCompany(company);
          } catch (err) {
            console.error('Error fetching current company record:', err);
            setCurrentCompany(null);
          }
        } else {
          setCurrentCompany(null);
        }

        // Update localStorage pocketbase_auth so legacy code that reads CompanyID keeps working
        if (initialCompanyId) {
          try {
            const updatedAuth = {
              ...pbAuth,
              record: {
                ...userRecord,
                CompanyID: initialCompanyId,
              },
            };
            localStorage.setItem('pocketbase_auth', JSON.stringify(updatedAuth));
          } catch (e) {
            console.error('Failed to update pocketbase_auth with current company ID', e);
          }
        }
      } catch (err) {
        console.error('Error loading companies for user:', err);
        setError('Failed to load companies');
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, []);

  const switchCompany = async (companyId) => {
    setCurrentCompanyId(companyId);

    try {
      const company = await pb.collection('Customers').getOne(companyId);
      setCurrentCompany(company);
    } catch (err) {
      console.error('Error fetching switched company record:', err);
      setCurrentCompany(null);
    }

    // Also persist in localStorage so existing code depending on CompanyID sees the new company
    try {
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const userRecord = pbAuth?.record || {};
      const updatedAuth = {
        ...pbAuth,
        record: {
          ...userRecord,
          CompanyID: companyId,
        },
      };
      localStorage.setItem('pocketbase_auth', JSON.stringify(updatedAuth));
    } catch (e) {
      console.error('Failed to update pocketbase_auth on company switch', e);
    }
  };

  const otherCompanies = accessibleCompanies.filter(
    (company) => company.id !== currentCompanyId
  );

  const value = {
    currentCompanyId,
    currentCompany,
    accessibleCompanies,
    otherCompanies,
    switchCompany,
    loading,
    error,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => useContext(CompanyContext);


